import { IS_ANDROID_APP } from '../utils/platform';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  Trophy, 
  Clock, 
  Target, 
  TrendingUp,
  Play,
  Crown,
  Star,
  Lock,
  Check,
  ChevronRight,
  AlertTriangle
} from 'lucide-react';
import Navbar from '../components/Navbar';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOTAL_TECHNIQUES = 17;
const MASTERY_THRESHOLD = 3;

const DIFFICULTY_COLORS = {
  beginner: '#34C759',
  easy: '#8B5A3C',
  medium: '#FF9F0A',
  hard: '#FF453A',
  expert: '#AF52DE',
};

function getTechniqueCategory(id) {
  if (id <= 2) return { label: 'Beginner', color: DIFFICULTY_COLORS.beginner };
  if (id <= 4) return { label: 'Easy', color: DIFFICULTY_COLORS.easy };
  if (id <= 7) return { label: 'Medium', color: DIFFICULTY_COLORS.medium };
  if (id <= 13) return { label: 'Hard', color: DIFFICULTY_COLORS.hard };
  return { label: 'Expert', color: DIFFICULTY_COLORS.expert };
}

// Extracted Components
function StatCard({ icon: Icon, iconColor, value, label }) {
  return (
    <Card className="bg-[var(--background-surface)] border-[var(--border-subtle)]">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div 
            className="w-10 h-10 rounded flex items-center justify-center"
            style={{ backgroundColor: `${iconColor}20` }}
          >
            <Icon className="w-5 h-5" style={{ color: iconColor }} />
          </div>
          <div>
            <p className="text-2xl font-bold text-[var(--text-primary)] font-mono">{value}</p>
            <p className="text-xs text-[var(--text-secondary)]">{label}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function TechniqueItem({ technique, formatTime, onPlay }) {
  const category = getTechniqueCategory(technique.id);
  const progress = Math.min((technique.solved_count / MASTERY_THRESHOLD) * 100, 100);
  
  return (
    <div 
      className="group relative flex items-center gap-3 p-3 rounded-lg transition-all duration-200 hover:bg-[var(--hover-bg-subtle)]"
      data-testid={`technique-${technique.id}`}
    >
      {/* Mastery indicator */}
      <div 
        className={`relative flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center font-mono text-sm font-bold transition-all duration-300 ${
          technique.mastered 
            ? 'shadow-[0_0_12px_rgba(52,199,89,0.3)]' 
            : ''
        }`}
        style={{ 
          backgroundColor: technique.mastered ? `${category.color}20` : 'var(--hover-bg-subtle)',
          border: `1.5px solid ${technique.mastered ? category.color : 'var(--border-subtle)'}`,
          color: technique.mastered ? category.color : 'var(--text-disabled)',
        }}
      >
        {technique.mastered ? (
          <Check className="w-5 h-5" />
        ) : (
          <span>{technique.id}</span>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="text-sm font-semibold text-[var(--text-primary)] truncate">{technique.name}</span>
          <span 
            className="text-[10px] font-mono px-1.5 py-0.5 rounded-full flex-shrink-0"
            style={{ 
              backgroundColor: `${category.color}15`,
              color: category.color,
            }}
          >
            {category.label}
          </span>
          {/* Classic-style badge — levels 1-7 are solvable using the
              standard human-solver toolkit (naked/hidden singles/pairs/
              triples, pointing pair, box-line reduction, X-Wing). L8+
              require chain / fish techniques the user marked "advanced". */}
          {technique.id <= 7 && (
            <span
              className="text-[10px] font-mono px-1.5 py-0.5 rounded-full flex-shrink-0 border border-[#8B5A3C]/40 text-[#8B5A3C] bg-[#8B5A3C]/8"
              data-testid={`classic-badge-${technique.id}`}
              title="Solvable using classic human-solver techniques"
            >
              Classic
            </span>
          )}
        </div>
        
        {/* Mini progress bar */}
        <div className="flex items-center gap-2">
          <div className="flex-1 h-1.5 bg-[var(--hover-bg-subtle)] rounded-full overflow-hidden">
            <div 
              className="h-full rounded-full transition-all duration-500"
              style={{ 
                width: `${progress}%`,
                backgroundColor: technique.mastered ? category.color : `${category.color}80`,
              }}
            />
          </div>
          <span className="text-[10px] font-mono text-[var(--text-muted)] flex-shrink-0 w-8 text-right">
            {technique.solved_count}/{MASTERY_THRESHOLD}
          </span>
        </div>
      </div>

      {/* Best time or play button */}
      <div className="flex-shrink-0 w-16 text-right">
        {technique.best_time ? (
          <span className="text-xs font-mono text-[var(--text-secondary)]">{formatTime(technique.best_time)}</span>
        ) : (
          <button 
            onClick={() => onPlay(technique.id)}
            className="opacity-0 group-hover:opacity-100 transition-opacity text-[#8B5A3C] hover:text-[#A46944]"
            data-testid={`play-technique-${technique.id}`}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

function TechniqueMasteryCard({ stats, masteryProgress, formatTime, onPlayTechnique }) {
  const [showAll, setShowAll] = useState(false);
  const masteryDetails = stats?.mastery_details || [];
  const techniquesMastered = stats?.techniques_mastered || 0;
  
  const displayedTechniques = showAll ? masteryDetails : masteryDetails.slice(0, 6);

  return (
    <Card className="bg-[var(--background-surface)] border-[var(--border-subtle)] col-span-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-[var(--text-primary)] font-heading flex items-center gap-2">
            <Star className="w-5 h-5 text-[#FF9F0A]" />
            Technique Mastery
          </CardTitle>
          <span className="text-sm font-mono text-[var(--text-secondary)]">
            <span className="text-[var(--text-primary)] font-bold">{techniquesMastered}</span>/{TOTAL_TECHNIQUES} mastered
          </span>
        </div>
        
        {/* Overall progress */}
        <div className="mt-3">
          <div className="relative h-2.5 bg-[var(--hover-bg-subtle)] rounded-full overflow-hidden">
            <div 
              className="absolute inset-y-0 left-0 rounded-full transition-all duration-700 ease-out"
              style={{ 
                width: `${masteryProgress}%`,
                background: `linear-gradient(90deg, ${DIFFICULTY_COLORS.beginner}, ${DIFFICULTY_COLORS.easy}, ${DIFFICULTY_COLORS.medium}, ${DIFFICULTY_COLORS.hard}, ${DIFFICULTY_COLORS.expert})`,
              }}
            />
          </div>
          <p className="text-[11px] text-[var(--text-disabled)] mt-1.5 font-mono">
            Solve each technique {MASTERY_THRESHOLD}+ times to master it
          </p>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        {masteryDetails.length > 0 ? (
          <>
            <div className="space-y-0.5" data-testid="mastery-list">
              {displayedTechniques.map(technique => (
                <TechniqueItem 
                  key={technique.id} 
                  technique={technique} 
                  formatTime={formatTime}
                  onPlay={onPlayTechnique}
                />
              ))}
            </div>
            
            {masteryDetails.length > 6 && (
              <button
                onClick={() => setShowAll(!showAll)}
                className="w-full mt-3 py-2 text-sm text-[#8B5A3C] hover:text-[#A46944] font-medium transition-colors"
                data-testid="show-all-techniques-btn"
              >
                {showAll ? 'Show Less' : `Show All ${TOTAL_TECHNIQUES} Techniques`}
              </button>
            )}
          </>
        ) : (
          <div className="text-center py-8">
            <Lock className="w-8 h-8 text-[var(--text-disabled)] mx-auto mb-3" />
            <p className="text-[var(--text-disabled)] text-sm">
              Complete puzzles to track your technique mastery
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function BestTimesCard({ stats, formatTime }) {
  const sortedBestTimes = useMemo(() => {
    return Object.entries(stats?.best_times || {})
      .sort(([a], [b]) => parseInt(a, 10) - parseInt(b, 10))
      .slice(0, 6);
  }, [stats?.best_times]);

  return (
    <Card className="bg-[var(--background-surface)] border-[var(--border-subtle)]">
      <CardHeader>
        <CardTitle className="text-[var(--text-primary)] font-heading flex items-center gap-2">
          <Clock className="w-5 h-5 text-[#34C759]" />
          Best Times
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {sortedBestTimes.map(([level, time]) => (
            <div key={level} className="flex justify-between items-center">
              <span className="text-[var(--text-secondary)]">Level {level}</span>
              <span className="text-[var(--text-primary)] font-mono text-lg">{formatTime(time)}</span>
            </div>
          ))}
          {sortedBestTimes.length === 0 && (
            <p className="text-[var(--text-disabled)] text-sm text-center py-4">
              Complete puzzles to set your best times
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function UpgradeCard({ onNavigate }) {
  return (
    <Card className="bg-gradient-to-r from-[#8B5A3C]/20 to-[#AF52DE]/20 border-[#8B5A3C]/30 mt-8">
      <CardContent className="p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h3 className="font-heading text-xl font-bold text-[var(--text-primary)] mb-1">
            Unlock Your Full Potential
          </h3>
          <p className="text-[var(--text-secondary)]">
            Get access to all 17 difficulty levels, unlimited puzzles, and more.
          </p>
        </div>
        <Button
          onClick={onNavigate}
          className="bg-[#8B5A3C] hover:bg-[#A46944] text-white whitespace-nowrap"
          data-testid="upgrade-btn"
        >
          <Crown className="w-4 h-4 mr-2" />
          Upgrade Now
        </Button>
      </CardContent>
    </Card>
  );
}

const TIER_LABELS = { free: 'Free', premium: 'Premium', premium_plus: 'Premium+' };

// Danger Zone — Delete Account (required for App Store / Play Store compliance)
function DeleteAccountCard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (confirmText.trim().toUpperCase() !== 'DELETE') {
      toast.error('Type DELETE to confirm.');
      return;
    }
    setDeleting(true);
    try {
      const { data } = await axios.delete(`${API_URL}/api/auth/account`, { withCredentials: true });
      toast.success('Account deleted. All your data has been removed.');
      // Best-effort: clear local auth state; cookies are already dropped server-side.
      try { await logout(); } catch (_e) { /* cookies already cleared */ }
      navigate('/', { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Unknown error';
      toast.error(`Delete failed: ${detail}`);
      setDeleting(false);
    }
  };

  if (!confirming) {
    return (
      <Card className="bg-[var(--background-surface)] border border-red-500/30 mt-6" data-testid="danger-zone-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-400 text-base">
            <AlertTriangle className="w-4 h-4" />
            Danger Zone
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-[var(--text-secondary)] mb-4">
            Permanently delete your account and every trace of it — stats, game history,
            leaderboard entries, referral records, and payment history. This cannot be undone.
          </p>
          <Button
            onClick={() => setConfirming(true)}
            variant="outline"
            className="border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300"
            data-testid="delete-account-open-btn"
          >
            <AlertTriangle className="w-4 h-4 mr-2" />
            Delete My Account
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-[var(--background-surface)] border border-red-500/50 mt-6" data-testid="delete-account-confirm-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-red-400 text-base">
          <AlertTriangle className="w-4 h-4" />
          Confirm account deletion
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-[var(--text-secondary)] mb-3">
          Type <span className="font-mono font-bold text-red-400">DELETE</span> below to
          permanently remove your account. You will be signed out immediately.
        </p>
        <input
          type="text"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder="Type DELETE to confirm"
          disabled={deleting}
          className="w-full px-3 py-2 rounded-md bg-[var(--background-elevated)] border border-red-500/40 text-[var(--text-primary)] mb-3 font-mono"
          data-testid="delete-account-confirm-input"
        />
        <div className="flex gap-2">
          <Button
            onClick={handleDelete}
            disabled={deleting || confirmText.trim().toUpperCase() !== 'DELETE'}
            className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-40"
            data-testid="delete-account-confirm-btn"
          >
            {deleting ? 'Deleting…' : 'Yes, delete forever'}
          </Button>
          <Button
            onClick={() => { setConfirming(false); setConfirmText(''); }}
            variant="outline"
            disabled={deleting}
            data-testid="delete-account-cancel-btn"
          >
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Main Component
export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/stats/me`, { withCredentials: true });
      setStats(data);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const reason = status ? `${status}${detail ? ` — ${detail}` : ''}` : (err?.message || 'network error');
      toast.error(`Failed to load stats (${reason})`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const formatTime = useCallback((seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  const masteryProgress = useMemo(() => {
    const mastered = stats?.techniques_mastered || 0;
    return (mastered / TOTAL_TECHNIQUES) * 100;
  }, [stats?.techniques_mastered]);

  const handleNavigateToSubscription = useCallback(() => {
    navigate('/subscription');
  }, [navigate]);

  const handleNavigateToPlay = useCallback(() => {
    navigate('/play');
  }, [navigate]);

  const handlePlayTechnique = useCallback((levelId) => {
    navigate(`/play?level=${levelId}`);
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: 'var(--background-main)' }}>
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="w-12 h-12 border-4 border-[#8B5A3C]/30 border-t-[#8B5A3C] rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background-main)' }}>
      <Navbar />
      
      <main className="max-w-6xl mx-auto px-4 py-8" data-testid="dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[var(--text-primary)]">
              Welcome back, {user?.name || 'Player'}!
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-[var(--text-secondary)]">
                {TIER_LABELS[user?.subscription_tier] || 'Free'} Member
              </span>
              {user?.subscription_tier !== 'free' && (
                <Crown className="w-4 h-4 text-[#FF9F0A]" />
              )}
            </div>
          </div>
          <Button
            onClick={handleNavigateToPlay}
            className="bg-[#8B5A3C] hover:bg-[#A46944] text-white font-semibold"
            data-testid="play-now-btn"
          >
            <Play className="w-4 h-4 mr-2" />
            Play Now
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard 
            icon={Target} 
            iconColor="#8B5A3C" 
            value={stats?.puzzles_completed || 0} 
            label="Puzzles Completed" 
          />
          <StatCard 
            icon={Clock} 
            iconColor="#34C759" 
            value={formatTime(stats?.total_time || 0)} 
            label="Total Time" 
          />
          <StatCard 
            icon={Trophy} 
            iconColor="#FF9F0A" 
            value={stats?.techniques_mastered || 0} 
            label="Techniques Mastered" 
          />
          <StatCard 
            icon={TrendingUp} 
            iconColor="#AF52DE" 
            value={Object.keys(stats?.best_times || {}).length} 
            label="Levels Mastered" 
          />
        </div>

        {/* Technique Mastery - Full Width */}
        <div className="mb-6">
          <TechniqueMasteryCard 
            stats={stats} 
            masteryProgress={masteryProgress} 
            formatTime={formatTime}
            onPlayTechnique={handlePlayTechnique}
          />
        </div>

        {/* Best Times */}
        <div className="grid lg:grid-cols-2 gap-6">
          <BestTimesCard stats={stats} formatTime={formatTime} />
        </div>

        {/* Upgrade CTA for free users */}
        {!IS_ANDROID_APP && user?.subscription_tier === 'free' && (
          <UpgradeCard onNavigate={handleNavigateToSubscription} />
        )}

        {/* Danger Zone — Account Deletion (App Store / Play Store compliance).
            Admins cannot self-delete; the backend rejects those requests. */}
        {user?.role !== 'admin' && <DeleteAccountCard />}
      </main>
    </div>
  );
}
